import { Children, isValidElement, type ReactElement, type ReactNode } from "react";
import { usePageModules } from "@/hooks/usePageModules";

/** Marks a page section that admins can switch on/off and reorder. */
export function Module({ children }: { id: string; children: ReactNode }) {
  return <>{children}</>;
}

/**
 * Renders <Module id> children in the admin-defined order, hiding disabled ones.
 * Non-Module children (navbar, dialogs, footer) keep their position: anything
 * before the first Module renders first, anything after the last renders last.
 */
export function ModularPage({ slug, children }: { slug: string; children: ReactNode }) {
  const { data, isFetched } = usePageModules(slug);
  const items = Children.toArray(children);
  const isModule = (c: unknown): c is ReactElement<{ id: string }> => isValidElement(c) && c.type === Module;
  const first = items.findIndex(isModule);
  const last = items.length - 1 - [...items].reverse().findIndex(isModule);
  if (first === -1) return <>{items}</>;

  const before = items.slice(0, first);
  const after = items.slice(last + 1);
  const modules = items.slice(first, last + 1).filter(isModule);

  let ordered = modules;
  if (data) {
    const order = new Map(data.map((m) => [m.key, m]));
    ordered = modules
      .filter((m) => order.get(m.props.id)?.enabled ?? true)
      .sort((a, b) => (order.get(a.props.id)?.sort_order ?? 0) - (order.get(b.props.id)?.sort_order ?? 0));
  }

  return (
    <>
      {before}
      {isFetched ? ordered : <div className="min-h-screen" />}
      {after}
    </>
  );
}
